import { differenceInCalendarDays, format, parseISO } from 'date-fns';

export const parseApiLocalDate = (dateStr: string | null | undefined): Date | null => {
  // Use for parsing API Date to js date object
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
};

export const parseFormDate = (dateStr: Date | null): string | null => {
  // Use to parse form date to an iso api date string
  if (!dateStr) return null;
  const year = dateStr.getFullYear();
  const month = String(dateStr.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const day = String(dateStr.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface FormatApiDateOptions {
  dateFormat?: string;
  prefix?: string;
  emptyLabel?: string;
}

export const formatApiDate = (
  // Use to format API Date fields and avoid timezone shift issues, will display the date exactly as returned in API
  // Optional prefix label E.g. 'Starts on' and Optional empty label E.g. '-' or 'No Start Date'
  dateStr: string | null | undefined,
  { dateFormat = 'MMM d, yyyy', prefix, emptyLabel = '-' }: FormatApiDateOptions = {}
): string => {
  const date = parseApiLocalDate(dateStr);
  if (!date) return emptyLabel;
  const formatted = format(date, dateFormat);
  return prefix ? `${prefix} ${formatted}` : formatted;
};

export const formatDateTo24Hr = (
  // Formats a date string or Date object to 24-hour time format
  // Accepts an optional format string, defaults to 'MM/dd/yyyy HH:mm'
  dateInput: string | Date | null | undefined,
  dateFormat: string = 'MM/dd/yyyy HH:mm'
): string => {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return '';
  return format(date, dateFormat);
};

export interface DateTemporalityOptions {
  // Apply warning color when the date is within this many days from today (inclusive)
  warningWithinDays?: number; // default: 30
  // Anything below this (i.e., negative daysUntil) is considered critical. Default 0 (past dates)
  criticalUpperLimitDays?: number; // default: 0
  // Tailwind classes for each state (override if needed)
  warningClass?: string; // default: 'tw:text-amber-600'
  criticalClass?: string; // default: 'tw:text-red-600'
}

/**
 * Returns a Tailwind class name based on how close the given date is to today.
 * - Past dates (daysUntil < criticalUpperLimitDays) → criticalClass (red)
 * - Dates within warningWithinDays (daysUntil <= warningWithinDays) → warningClass (amber)
 * - Otherwise → undefined (no additional color)
 *
 * Accepts API local dates (YYYY-MM-DD), full ISO strings, or Date objects.
 */
export const getDateTemporalityClass = (
  dateInput: string | Date | null | undefined,
  options: DateTemporalityOptions = {}
): string | undefined => {
  const {
    warningWithinDays = 30,
    criticalUpperLimitDays = 0,
    warningClass = 'tw:text-stoplight-warning',
    criticalClass = 'tw:text-stoplight-danger',
  } = options;

  if (!dateInput) return undefined;

  // Try to parse API local date first to avoid timezone shifts; fallback to Date parsing
  let date: Date | null = null;
  if (typeof dateInput === 'string') {
    date = parseApiLocalDate(dateInput) ?? new Date(dateInput);
  } else {
    date = dateInput;
  }

  if (!date || isNaN(date.getTime())) return undefined;

  const daysUntil = differenceInCalendarDays(date, new Date());

  if (daysUntil < criticalUpperLimitDays) return criticalClass;
  if (daysUntil <= warningWithinDays) return warningClass;
  return undefined;
};

/**
 * Formats a duration string (HH:MM:SS) into a human-readable format like "12h" or "8h 30m".
 */
export const formatDuration = (duration: string | null | undefined): string => {
  if (!duration) return '--';
  // DRF DurationField serializes as "D HH:MM:SS" for days or "HH:MM:SS" for hours only
  const match = duration.match(/(?:(\d+)\s+)?(\d+):(\d+):(\d+)/);
  if (match) {
    const days = parseInt(match[1] || '0', 10);
    const hours = days * 24 + parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  return duration;
};

/**
 * Formats a shift's date/time range for display.
 * Shows end date only when it differs from start date (overnight shifts).
 *
 * @param startOn - Start date string (YYYY-MM-DD)
 * @param startTime - Start time string (HH:mm:ss)
 * @param endOn - End date string (YYYY-MM-DD)
 * @param endTime - End time string (HH:mm:ss)
 * @param dateFormat - date-fns format for dates (default: 'EEE MMM dd, yyyy')
 * @param timeFormat - date-fns format for times (default: 'HH:mm')
 */
export const formatShiftDateRange = (
  startOn: string,
  startTime: string,
  endOn: string,
  endTime: string,
  dateFormat: string = 'EEE MMM dd, yyyy',
  timeFormat: string = 'HH:mm'
): string => {
  const startDateStr = format(parseISO(startOn), dateFormat);
  const startTimeStr = format(parseISO(`2000-01-01T${startTime}`), timeFormat);
  const endTimeStr = format(parseISO(`2000-01-01T${endTime}`), timeFormat);
  const endDateStr = endOn !== startOn ? format(parseISO(endOn), dateFormat) : null;

  if (endDateStr) {
    return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
  }
  return `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
};
